import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Plus, Type, Square, Image as ImageIcon, Save, Download, Trash2, Sparkles, Upload,
  Loader2, Copy, Layers, Wand2, CheckCircle2, ArrowUp, ArrowDown, FolderOpen, Printer
} from 'lucide-react';
import { toPng } from 'html-to-image';
import { suiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Panel, Card, Field, EmptyState, inputCls } from './shared';
import { toast } from 'sonner';

const FONTS = ['Inter', 'Georgia', 'Impact', 'Courier New', 'Trebuchet MS', 'Times New Roman'];
const uid = () => Math.random().toString(36).slice(2, 10);

const blank = (w = 1080, h = 1080) => ({
  name: 'Untitled Design', width: w, height: h, background: '#ffffff', layers: [],
});

export default function DesignStudio({ campaigns = [] }) {
  const [presets, setPresets] = useState({ sizes: [], templates: [] });
  const [designs, setDesigns] = useState([]);
  const [design, setDesign] = useState(blank());
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState('');
  const [aiBrief, setAiBrief] = useState('');
  const [imgPrompt, setImgPrompt] = useState('');
  const [assets, setAssets] = useState([]);
  const [showAssets, setShowAssets] = useState(false);
  const [bleed, setBleed] = useState(3);
  const [qrId, setQrId] = useState('');
  const [qrList, setQrList] = useState([]);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  const displayW = 620;
  const scale = displayW / design.width;

  const loadAll = useCallback(() => {
    suiteAPI.designPresets().then(r => setPresets(r.data));
    suiteAPI.listDesigns().then(r => setDesigns(r.data));
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const layer = design.layers.find(l => l.id === selected);
  const patch = (id, changes) => setDesign(d => ({ ...d, layers: d.layers.map(l => l.id === id ? { ...l, ...changes } : l) }));

  const addText = (role = 'headline') => {
    const l = {
      id: uid(), type: 'text', text: role === 'headline' ? 'Your headline here' : 'Supporting copy goes here',
      x: 60, y: role === 'headline' ? 80 : 240, w: design.width - 120, h: role === 'headline' ? 140 : 90,
      fontSize: role === 'headline' ? 84 : 34, fontFamily: 'Inter', color: '#111111',
      align: 'left', weight: role === 'headline' ? 'bold' : 'normal', rotation: 0, opacity: 1,
    };
    setDesign(d => ({ ...d, layers: [...d.layers, l] }));
    setSelected(l.id);
  };

  const addRect = () => {
    const l = { id: uid(), type: 'rect', x: 80, y: 400, w: 320, h: 90, fill: '#b8ff00', radius: 999, rotation: 0, opacity: 1 };
    setDesign(d => ({ ...d, layers: [...d.layers, l] }));
    setSelected(l.id);
  };

  const addImage = (src, natural) => {
    const w = Math.min(design.width, natural?.w || design.width);
    const l = { id: uid(), type: 'image', src, x: 0, y: 0, w, h: natural?.h ? (natural.h * w) / natural.w : w, rotation: 0, opacity: 1 };
    setDesign(d => ({ ...d, layers: [...d.layers, l] }));
    setSelected(l.id);
  };

  const onPointerDown = (e, l, mode = 'move') => {
    e.stopPropagation();
    setSelected(l.id);
    dragRef.current = { id: l.id, mode, startX: e.clientX, startY: e.clientY, orig: { ...l } };
  };

  useEffect(() => {
    const move = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = (e.clientX - d.startX) / scale;
      const dy = (e.clientY - d.startY) / scale;
      if (d.mode === 'move') patch(d.id, { x: Math.round(d.orig.x + dx), y: Math.round(d.orig.y + dy) });
      else patch(d.id, { w: Math.max(24, Math.round(d.orig.w + dx)), h: Math.max(24, Math.round(d.orig.h + dy)) });
    };
    const up = () => { dragRef.current = null; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [scale]);

  const save = async () => {
    setBusy('save');
    try {
      const thumb = await toPng(canvasRef.current, { pixelRatio: 0.25, cacheBust: true }).catch(() => null);
      const { data } = await suiteAPI.saveDesign({ ...design, thumbnail: thumb });
      setDesign(data);
      loadAll();
      toast.success('Design saved');
    } catch { toast.error('Could not save design'); }
    finally { setBusy(''); }
  };

  const exportPng = async (toLibrary) => {
    setBusy('export');
    try {
      const dataUrl = await toPng(canvasRef.current, { pixelRatio: 2, cacheBust: true });
      if (toLibrary) {
        let id = design.id;
        if (!id) {
          const { data } = await suiteAPI.saveDesign(design);
          id = data.id; setDesign(data);
        }
        await suiteAPI.exportDesign(id, { data_url: dataUrl });
        toast.success('Saved to asset library');
      } else {
        const a = document.createElement('a');
        a.href = dataUrl; a.download = `${design.name || 'design'}.png`; a.click();
      }
    } catch { toast.error('Export failed'); }
    finally { setBusy(''); }
  };

  const aiLayout = async () => {
    if (!aiBrief.trim()) return toast.error('Describe what this design is for');
    setBusy('ai');
    try {
      const { data } = await suiteAPI.aiDesignLayout({ brief: aiBrief, width: design.width, height: design.height });
      setDesign(d => ({ ...d, background: data.background, layers: [...d.layers, ...data.layers] }));
      toast.success('AI layout added — tweak anything you like');
    } catch { toast.error('AI layout failed'); }
    finally { setBusy(''); }
  };

  const aiImage = async () => {
    if (!imgPrompt.trim()) return toast.error('Describe the image you want');
    setBusy('img');
    try {
      const { data } = await suiteAPI.aiImage({ prompt: imgPrompt, kind: 'ad_creative' });
      addImage(suiteAPI.fileUrl(data.id), { w: design.width, h: design.width });
      toast.success('AI creative added to canvas');
    } catch (e) { toast.error(e.response?.data?.detail || 'Image generation failed'); }
    finally { setBusy(''); }
  };

  const importFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy('import');
    const fd = new FormData();
    fd.append('file', f);
    fd.append('source', 'canva');
    try {
      const { data } = await suiteAPI.importDesign(fd);
      setDesign(data);
      setSelected(data.layers?.[0]?.id || null);
      loadAll();
      toast.success('Imported — keep editing right here');
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setBusy(''); }
  };

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    fd.append('folder', 'Design Uploads');
    try {
      const { data } = await suiteAPI.uploadAsset(fd);
      addImage(suiteAPI.fileUrl(data.id));
      toast.success('Image added');
    } catch { toast.error('Upload failed'); }
  };

  const openAssets = async () => {
    const { data } = await suiteAPI.assetLibrary({});
    setAssets(data.assets.filter(a => (a.content_type || '').startsWith('image/')));
    setShowAssets(true);
  };

  const requestApproval = async () => {
    if (!design.id) return toast.error('Save the design first');
    await suiteAPI.requestApproval({ item_type: 'design', item_id: design.id, item_name: design.name });
    toast.success('Sent for approval');
  };

  const printPack = async () => {
    setBusy('print');
    try {
      let id = design.id;
      if (!id) {
        const { data } = await suiteAPI.saveDesign(design);
        id = data.id; setDesign(data);
      }
      const dataUrl = await toPng(canvasRef.current, { pixelRatio: 2, cacheBust: true });
      const res = await suiteAPI.printPack(id, { data_url: dataUrl, bleed_mm: bleed, dpi: 300, qr_id: qrId || null });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${design.name || 'design'}-print-pack.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Print pack saved to your asset library and downloaded');
    } catch { toast.error('Print pack failed'); }
    finally { setBusy(''); }
  };

  const move = (dir) => {
    const idx = design.layers.findIndex(l => l.id === selected);
    if (idx < 0) return;
    const to = idx + dir;
    if (to < 0 || to >= design.layers.length) return;
    const arr = [...design.layers];
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    setDesign(d => ({ ...d, layers: arr }));
  };

  return (
    <Panel
      title="Design Studio"
      subtitle="A full drag-and-drop design canvas. Start blank, from a size preset, from AI, or import a design you already made in Canva or Figma and keep working on it here."
      testId="suite-design"
      action={
        <div className="flex gap-2 flex-wrap">
          <label className="inline-flex">
            <Button asChild className="bg-zinc-800 text-white" data-testid="design-import-btn">
              <span className="cursor-pointer flex items-center">{busy === 'import' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Upload size={14} className="mr-1.5" />} Import design</span>
            </Button>
            <input type="file" accept="image/*" onChange={importFile} className="hidden" />
          </label>
          <Button onClick={save} disabled={busy === 'save'} className="bg-zinc-800 text-white" data-testid="design-save-btn">
            {busy === 'save' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />} Save
          </Button>
          <Button onClick={() => exportPng(false)} disabled={busy === 'export'} className="bg-[#b8ff00] text-black font-bold" data-testid="design-export-btn">
            <Download size={14} className="mr-1.5" /> Download PNG
          </Button>
          <Button onClick={printPack} disabled={busy === 'print'} className="bg-zinc-800 text-white" data-testid="design-print-pack">
            {busy === 'print' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Printer size={14} className="mr-1.5" />} Print pack
          </Button>
        </div>
      }
    >
      <div className="grid xl:grid-cols-[240px_1fr_290px] gap-5">
        {/* LEFT: elements + sizes + library */}
        <div className="space-y-3">
          <Card className="p-4 space-y-2">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Add</p>
            <Button onClick={() => addText('headline')} className="w-full justify-start bg-zinc-800 text-white text-xs" data-testid="add-headline"><Type size={13} className="mr-2" /> Headline</Button>
            <Button onClick={() => addText('body')} className="w-full justify-start bg-zinc-800 text-white text-xs" data-testid="add-body"><Type size={13} className="mr-2" /> Body text</Button>
            <Button onClick={addRect} className="w-full justify-start bg-zinc-800 text-white text-xs" data-testid="add-shape"><Square size={13} className="mr-2" /> Shape / button</Button>
            <label className="block">
              <Button asChild className="w-full justify-start bg-zinc-800 text-white text-xs"><span className="cursor-pointer flex items-center"><ImageIcon size={13} className="mr-2" /> Upload image</span></Button>
              <input type="file" accept="image/*" onChange={uploadImage} className="hidden" />
            </label>
            <Button onClick={openAssets} className="w-full justify-start bg-zinc-800 text-white text-xs" data-testid="add-from-library"><FolderOpen size={13} className="mr-2" /> From library</Button>
          </Card>

          <Card className="p-4 space-y-2">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Canvas size</p>
            <select
              value={`${design.width}x${design.height}`}
              onChange={e => { const [w, h] = e.target.value.split('x').map(Number); setDesign(d => ({ ...d, width: w, height: h })); }}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-md text-white text-xs px-2 py-2"
              data-testid="design-size"
            >
              {presets.sizes.map(s => <option key={s.id} value={`${s.width}x${s.height}`}>{s.label} · {s.width}×{s.height}</option>)}
              <option value={`${design.width}x${design.height}`}>Custom {design.width}×{design.height}</option>
            </select>
            <Field label="Background">
              <input type="color" value={design.background} onChange={e => setDesign(d => ({ ...d, background: e.target.value }))} className="w-full h-8 bg-zinc-800 rounded" />
            </Field>
          </Card>

          <Card className="p-4 space-y-2">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Sparkles size={11} className="text-[#b8ff00]" /> AI</p>
            <Textarea value={aiBrief} onChange={e => setAiBrief(e.target.value)} rows={2} placeholder="Summer sale, 30% off, urban streetwear" className={`${inputCls} text-xs`} data-testid="design-ai-brief" />
            <Button onClick={aiLayout} disabled={busy === 'ai'} className="w-full bg-violet-500 text-white text-xs" data-testid="design-ai-layout">
              {busy === 'ai' ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Wand2 size={13} className="mr-1.5" />} AI layout
            </Button>
            <Textarea value={imgPrompt} onChange={e => setImgPrompt(e.target.value)} rows={2} placeholder="Neon-lit city street, model in bomber jacket" className={`${inputCls} text-xs`} data-testid="design-ai-image-prompt" />
            <Button onClick={aiImage} disabled={busy === 'img'} className="w-full bg-zinc-800 text-white text-xs" data-testid="design-ai-image">
              {busy === 'img' ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <ImageIcon size={13} className="mr-1.5" />} AI image
            </Button>
          </Card>

          <Card className="p-4">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">My designs</p>
            <div className="space-y-1 max-h-52 overflow-y-auto">
              <Button onClick={() => { setDesign(blank(design.width, design.height)); setSelected(null); }} className="w-full justify-start bg-zinc-800 text-white text-xs" data-testid="design-new"><Plus size={12} className="mr-1.5" /> New blank</Button>
              {designs.map(d => (
                <button key={d.id} onClick={() => suiteAPI.getDesign(d.id).then(r => { setDesign(r.data); setSelected(null); })}
                  className="w-full text-left px-2 py-1.5 rounded text-xs text-zinc-400 hover:bg-zinc-800 hover:text-white truncate" data-testid={`design-open-${d.id}`}>
                  {d.name}
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* CENTER: canvas */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Input value={design.name} onChange={e => setDesign(d => ({ ...d, name: e.target.value }))} className={`${inputCls} h-8 text-sm max-w-xs`} data-testid="design-name" />
            <span className="text-[11px] text-zinc-600">{design.width}×{design.height}px</span>
            <div className="ml-auto flex gap-1">
              <Button size="sm" variant="ghost" className="text-zinc-400" onClick={() => move(1)} title="Bring forward"><ArrowUp size={13} /></Button>
              <Button size="sm" variant="ghost" className="text-zinc-400" onClick={() => move(-1)} title="Send backward"><ArrowDown size={13} /></Button>
              <Button size="sm" variant="ghost" className="text-zinc-400" onClick={() => exportPng(true)} title="Save to asset library" data-testid="design-to-library"><Layers size={13} /></Button>
              <Button size="sm" variant="ghost" className="text-zinc-400" onClick={requestApproval} title="Request approval" data-testid="design-approval"><CheckCircle2 size={13} /></Button>
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 overflow-hidden flex justify-center">
            <div style={{ width: displayW, height: design.height * scale }} className="relative">
              <div
                ref={canvasRef}
                onClick={() => setSelected(null)}
                style={{
                  width: design.width, height: design.height, background: design.background,
                  transform: `scale(${scale})`, transformOrigin: 'top left', position: 'absolute', overflow: 'hidden',
                }}
                data-testid="design-canvas"
              >
                {design.layers.map(l => (
                  <div
                    key={l.id}
                    onMouseDown={(e) => onPointerDown(e, l)}
                    style={{
                      position: 'absolute', left: l.x, top: l.y, width: l.w, height: l.h,
                      transform: `rotate(${l.rotation || 0}deg)`, opacity: l.opacity ?? 1,
                      outline: selected === l.id ? '3px solid #b8ff00' : 'none', cursor: 'move',
                    }}
                  >
                    {l.type === 'text' && (
                      <div style={{
                        fontSize: l.fontSize, fontFamily: l.fontFamily, color: l.color, textAlign: l.align,
                        fontWeight: l.weight, lineHeight: 1.15, width: '100%', height: '100%', whiteSpace: 'pre-wrap',
                      }}>{l.text}</div>
                    )}
                    {l.type === 'rect' && (
                      <div style={{ width: '100%', height: '100%', background: l.fill, borderRadius: l.radius ?? 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: l.textColor || '#000',
                        fontSize: l.fontSize || 32, fontWeight: 'bold', fontFamily: l.fontFamily || 'Inter' }}>{l.text || ''}</div>
                    )}
                    {l.type === 'image' && (
                      <img src={l.src} alt="" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'cover' }} draggable={false} />
                    )}
                    {selected === l.id && (
                      <div
                        onMouseDown={(e) => onPointerDown(e, l, 'resize')}
                        style={{ position: 'absolute', right: -12, bottom: -12, width: 24, height: 24, background: '#b8ff00', borderRadius: 4, cursor: 'nwse-resize' }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: properties */}
        <div className="space-y-3">
          <Card className="p-4 space-y-3">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500">{layer ? `${layer.type} properties` : 'Select a layer'}</p>
            {!layer && <p className="text-xs text-zinc-600">Click any element on the canvas to edit it.</p>}
            {layer && (
              <>
                {(layer.type === 'text' || layer.type === 'rect') && (
                  <Field label="Text">
                    <Textarea value={layer.text || ''} onChange={e => patch(layer.id, { text: e.target.value })} rows={3} className={`${inputCls} text-xs`} data-testid="layer-text" />
                  </Field>
                )}
                {layer.type === 'text' && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <Field label="Size"><Input type="number" value={layer.fontSize} onChange={e => patch(layer.id, { fontSize: Number(e.target.value) })} className={`${inputCls} h-8`} /></Field>
                      <Field label="Color"><input type="color" value={layer.color} onChange={e => patch(layer.id, { color: e.target.value })} className="w-full h-8 bg-zinc-800 rounded" /></Field>
                    </div>
                    <Field label="Font">
                      <select value={layer.fontFamily} onChange={e => patch(layer.id, { fontFamily: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 rounded text-white text-xs px-2 py-1.5">
                        {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </Field>
                    <div className="flex gap-1">
                      {['left', 'center', 'right'].map(a => (
                        <Button key={a} size="sm" onClick={() => patch(layer.id, { align: a })} className={`flex-1 text-[11px] ${layer.align === a ? 'bg-[#b8ff00] text-black' : 'bg-zinc-800 text-zinc-400'}`}>{a}</Button>
                      ))}
                    </div>
                    <div className="flex gap-1">
                      {['normal', 'bold'].map(w => (
                        <Button key={w} size="sm" onClick={() => patch(layer.id, { weight: w })} className={`flex-1 text-[11px] ${layer.weight === w ? 'bg-[#b8ff00] text-black' : 'bg-zinc-800 text-zinc-400'}`}>{w}</Button>
                      ))}
                    </div>
                  </>
                )}
                {layer.type === 'rect' && (
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Fill"><input type="color" value={layer.fill} onChange={e => patch(layer.id, { fill: e.target.value })} className="w-full h-8 bg-zinc-800 rounded" /></Field>
                    <Field label="Radius"><Input type="number" value={layer.radius ?? 0} onChange={e => patch(layer.id, { radius: Number(e.target.value) })} className={`${inputCls} h-8`} /></Field>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <Field label="X"><Input type="number" value={Math.round(layer.x)} onChange={e => patch(layer.id, { x: Number(e.target.value) })} className={`${inputCls} h-8`} /></Field>
                  <Field label="Y"><Input type="number" value={Math.round(layer.y)} onChange={e => patch(layer.id, { y: Number(e.target.value) })} className={`${inputCls} h-8`} /></Field>
                  <Field label="Width"><Input type="number" value={Math.round(layer.w)} onChange={e => patch(layer.id, { w: Number(e.target.value) })} className={`${inputCls} h-8`} /></Field>
                  <Field label="Height"><Input type="number" value={Math.round(layer.h)} onChange={e => patch(layer.id, { h: Number(e.target.value) })} className={`${inputCls} h-8`} /></Field>
                  <Field label="Rotation"><Input type="number" value={layer.rotation || 0} onChange={e => patch(layer.id, { rotation: Number(e.target.value) })} className={`${inputCls} h-8`} /></Field>
                  <Field label="Opacity"><Input type="number" step="0.1" min="0" max="1" value={layer.opacity ?? 1} onChange={e => patch(layer.id, { opacity: Number(e.target.value) })} className={`${inputCls} h-8`} /></Field>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1 bg-zinc-800 text-white text-xs" onClick={() => {
                    const copy = { ...layer, id: uid(), x: layer.x + 24, y: layer.y + 24 };
                    setDesign(d => ({ ...d, layers: [...d.layers, copy] })); setSelected(copy.id);
                  }}><Copy size={12} className="mr-1" /> Duplicate</Button>
                  <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => { setDesign(d => ({ ...d, layers: d.layers.filter(x => x.id !== layer.id) })); setSelected(null); }} data-testid="layer-delete"><Trash2 size={13} /></Button>
                </div>
              </>
            )}
          </Card>

          <Card className="p-4">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Layers</p>
            {design.layers.length === 0 ? <p className="text-xs text-zinc-600">Nothing on the canvas yet.</p> : (
              <div className="space-y-1">
                {[...design.layers].reverse().map(l => (
                  <button key={l.id} onClick={() => setSelected(l.id)}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs truncate ${selected === l.id ? 'bg-[#b8ff00]/10 text-[#b8ff00]' : 'text-zinc-400 hover:bg-zinc-800'}`}>
                    {l.type === 'text' ? l.text?.slice(0, 24) : l.type === 'image' ? 'Image' : (l.text || 'Shape')}
                  </button>
                ))}
              </div>
            )}
          </Card>

          {campaigns.length > 0 && (
            <Card className="p-4">
              <Field label="Attach to campaign">
                <select value={design.campaign_id || ''} onChange={e => setDesign(d => ({ ...d, campaign_id: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded text-white text-xs px-2 py-1.5" data-testid="design-campaign">
                  <option value="">None</option>
                  {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
            </Card>
          )}

          <Card className="p-4 space-y-2">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Printer size={11} /> Print pack</p>
            <Field label="Bleed (mm)"><Input type="number" value={bleed} onChange={e => setBleed(Number(e.target.value))} className="bg-zinc-800 border-zinc-700 text-white h-8" data-testid="print-bleed" /></Field>
            <Field label="Include campaign QR">
              <select value={qrId} onChange={e => setQrId(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded text-white text-xs px-2 py-1.5" data-testid="print-qr">
                <option value="">None</option>
                {qrList.map(q => <option key={q.id} value={q.id}>{q.name}</option>)}
              </select>
            </Field>
            <p className="text-[10px] text-zinc-600">Produces a 300dpi PDF with bleed, crop marks and a printer spec sheet.</p>
          </Card>

        </div>
      </div>

      {showAssets && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => setShowAssets(false)}>
          <Card className="p-5 max-w-3xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-white mb-3">Asset library</h3>
            {assets.length === 0 ? <EmptyState icon={ImageIcon} text="No images in your library yet." /> : (
              <div className="grid grid-cols-4 gap-3">
                {assets.map(a => (
                  <button key={a.id} onClick={() => { addImage(suiteAPI.fileUrl(a.id)); setShowAssets(false); }} className="group" data-testid={`asset-pick-${a.id}`}>
                    <img src={suiteAPI.fileUrl(a.id)} alt="" className="w-full aspect-square object-cover rounded-lg border border-zinc-800 group-hover:border-[#b8ff00]" />
                    <p className="text-[10px] text-zinc-500 truncate mt-1">{a.name}</p>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </Panel>
  );
}
